import type { AuditLogQuery, AuditLogRow, HqApi } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { sumWidth, TipText } from '@hq/ui-kit';
import { formatDisplayTime } from '@hq/utils';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';

interface FilterValues {
  bizType?: string;
  action?: string;
  userId?: string;
  orgId?: string;
  /** 平台侧按租户过滤（留空 = 全平台）。 */
  tenantId?: string;
  range?: [Dayjs, Dayjs];
}

const TERMINAL_COLOR: Record<string, string> = {
  WEB: 'blue',
  APP: 'purple',
  PDA: 'gold',
  SYSTEM: 'default'
};

export interface AuditLogsPageProps {
  hq: HqApi;
  /**
   * 平台视角（web-system）：多「租户」筛选与列，可跨租户举证；
   * 运营端/院内视角固定本租户，不展示租户维度。
   */
  platform?: boolean;
}

/**
 * 审计日志（`GET /api/sys/audit`，门禁 sys:audit:view）。
 *
 * 审计要求留存 ≥6 个月且可举证导出：这里提供按时间/业务/操作人检索、游标翻页与 CSV 导出
 * （导出动作本身也会写审计）。ops / system 共用本实现，仅 `platform` 不同。
 */
export function AuditLogsPage({ hq, platform = false }: AuditLogsPageProps) {
  const { message } = App.useApp();
  const [filter, setFilter] = useState<AuditLogQuery>({ size: 20 });
  const [page, setPage] = useState(1);
  const [lastIds, setLastIds] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const { data, isFetching, error } = useQuery({
    queryKey: ['audit', filter, page],
    queryFn: () => hq.audit.list(filter)
  });

  const summary = useQuery({
    queryKey: ['audit', 'summary', filter.beginTime, filter.endTime, filter.tenantId],
    queryFn: () => hq.audit.summary(filter.beginTime, filter.endTime, filter.tenantId)
  });

  // 租户下拉：只读审计员可能没有 sys:tenant:view，取不到就回退为手填 ID（不阻断审计查询）
  const tenantOptions = useQuery({
    queryKey: ['audit', 'tenant-options'],
    queryFn: () => hq.tenant.list({ pageSize: 200 }),
    enabled: platform,
    retry: false,
    staleTime: 5 * 60 * 1000
  });
  const tenantSelectOptions = (tenantOptions.data ?? []).map((item) => ({
    value: item.id,
    label: `${item.tenantName}（${item.tenantCode}）`
  }));

  const onSearch = (values: FilterValues) => {
    setPage(1);
    setLastIds([]);
    setFilter({
      size: 20,
      bizType: values.bizType?.trim() || undefined,
      action: values.action?.trim() || undefined,
      userId: values.userId?.trim() || undefined,
      orgId: values.orgId?.trim() || undefined,
      tenantId: platform ? values.tenantId?.trim() || undefined : undefined,
      // 用本地 yyyy-MM-dd HH:mm:ss 而不是 toISOString()：
      // toISOString 输出 UTC，服务端按本地时间解析，客户端/服务端时区不同时会整体偏移一天，
      // 查出来的区间和界面上选的对不上（本项目时间参数统一走本地格式）。
      beginTime: values.range?.[0]?.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      endTime: values.range?.[1]?.endOf('day').format('YYYY-MM-DD HH:mm:ss')
    });
  };

  const rows = data?.items ?? [];

  /** 导出 CSV（`GET /api/sys/audit/export`，DR-8.9 AUDIT_ADMIN 只读+导出）。 */
  const onExport = async () => {
    setExporting(true);
    try {
      // 走 SDK（保留令牌注入、超时、401 统一处理），不再裸 fetch 拼鉴权
      const csv = await hq.audit.exportCsv(filter);

      // 带 BOM：Excel 直接打开中文不乱码
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-${dayjs().format('YYYYMMDDHHmmss')}.csv`;
      link.click();
      // 立刻 revoke 会让部分浏览器中断下载，延迟释放
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success('已开始下载（导出动作已记入审计）');
    } catch (err) {
      message.error(`导出失败：${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExporting(false);
    }
  };

  // 列定义抽成常量，用 sumWidth 核算横向宽度（写死数值会导致列宽被压缩）
  const columns: ColumnsType<AuditLogRow> = [
    { title: '时间', dataIndex: 'actAt', width: 175, render: (value: string | null) => formatDisplayTime(value) },
    ...(platform
      ? [
          {
            title: '租户',
            dataIndex: 'tenantId',
            width: 120,
            render: (v: string) => (v && v !== '0' ? <Tag color="blue">{v}</Tag> : <Tag>平台</Tag>)
          } as ColumnsType<AuditLogRow>[number]
        ]
      : []),
    {
      title: '业务类型',
      dataIndex: 'bizType',
      width: 140,
      ellipsis: { showTitle: false },
      render: (v: string | null) => (v ? <TipText text={v} /> : '-')
    },
    {
      title: '业务单号',
      dataIndex: 'bizNo',
      width: 200,
      ellipsis: { showTitle: false },
      render: (v: string | null) => (v ? <TipText text={v} /> : '-')
    },
    { title: '操作', dataIndex: 'action', width: 140, render: (v: string) => <Tag>{v}</Tag> },
    {
      title: '操作人',
      dataIndex: 'userName',
      width: 140,
      ellipsis: { showTitle: false },
      render: (v: string | null, row) => <TipText text={v ?? row.userId} />
    },
    {
      title: '角色',
      dataIndex: 'roleCodes',
      width: 160,
      ellipsis: { showTitle: false },
      render: (v: string | null) => (v ? <TipText text={v} /> : '-')
    },
    { title: '机构 ID', dataIndex: 'orgId', width: 150 },
    {
      title: '终端',
      dataIndex: 'terminal',
      width: 100,
      render: (v: string | null) => (v ? <Tag color={TERMINAL_COLOR[v] ?? 'default'}>{v}</Tag> : '-')
    },
    { title: 'IP', dataIndex: 'ip', width: 130 },
    {
      title: '备注',
      dataIndex: 'remark',
      ellipsis: { showTitle: false },
      render: (v: string | null) => (v ? <TipText text={v} /> : '-')
    }
  ];

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        审计日志
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {platform ? '平台侧跨租户举证查询' : '本院操作留痕（只读）'}；时间区间由后端换算为主键区间，按主键倒序游标翻页。
      </Typography.Paragraph>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error instanceof ApiError ? error.friendlyMessage : String(error)}
        />
      ) : null}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={12} md={6}>
            <Statistic title="命中总条数（当前筛选）" value={summary.data?.total ?? 0} />
          </Col>
          <Col xs={24} md={18}>
            <Space wrap size={[8, 8]}>
              {Object.entries(summary.data?.byAction ?? {}).map(([action, count]) => (
                <Tag key={action} color="blue">
                  {action} · {count}
                </Tag>
              ))}
            </Space>
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Form<FilterValues> layout="inline" onFinish={onSearch}>
          <Form.Item name="bizType" label="业务类型">
            <Input allowClear placeholder="如 WL_APPLY" style={{ width: 150 }} />
          </Form.Item>
          <Form.Item name="action" label="操作">
            <Input allowClear placeholder="如 APPROVE" style={{ width: 130 }} />
          </Form.Item>
          <Form.Item name="userId" label="操作人 ID">
            <Input allowClear style={{ width: 140 }} />
          </Form.Item>
          <Form.Item name="orgId" label="机构 ID">
            <Input allowClear style={{ width: 130 }} />
          </Form.Item>
          {platform ? (
            <Form.Item name="tenantId" label="租户">
              {tenantSelectOptions.length > 0 ? (
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="留空=全平台"
                  style={{ width: 240 }}
                  options={tenantSelectOptions}
                />
              ) : (
                <Input allowClear placeholder="留空=全平台（可填租户 ID）" style={{ width: 200 }} />
              )}
            </Form.Item>
          ) : null}
          <Form.Item name="range" label="时间">
            <DatePicker.RangePicker allowEmpty={[true, true]} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isFetching}>
              查询
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={() => void onExport()} loading={exporting}>
              导出 CSV
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Table<AuditLogRow>
        size="small"
        rowKey="id"
        loading={isFetching}
        dataSource={rows}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
        columns={columns}
      />

      <Space style={{ marginTop: 16 }}>
        <Button
          disabled={page === 1}
          onClick={() => {
            // 游标翻页：回退时必须把 lastId 写回 filter，否则「上一页」仍请求最后一页的数据
            const next = lastIds.slice(0, -1);
            setLastIds(next);
            setFilter((prev) => ({ ...prev, lastId: next[next.length - 1] }));
            setPage((p) => Math.max(1, p - 1));
          }}
        >
          上一页
        </Button>
        <Typography.Text type="secondary">第 {page} 页</Typography.Text>
        <Button
          disabled={!data?.hasMore || !data.nextLastId}
          onClick={() => {
            const next = data?.nextLastId;
            if (!next) return;
            setLastIds((prev) => [...prev, next]);
            setFilter((prev) => ({ ...prev, lastId: next }));
            setPage((p) => p + 1);
          }}
        >
          下一页
        </Button>
        {lastIds.length > 0 ? <Typography.Text type="secondary">（游标翻页）</Typography.Text> : null}
      </Space>
    </div>
  );
}
